// src/components/ZoneSection.js
import React, { useState, useMemo } from 'react';

import Footer from './Footer.jsx';
import TrenchCreatures from './zones/ThalwegTrench/ThalwegCreatures.jsx'; // fixed filename
import DeepCreatures from './zones/DeepPools/DeepCreatures.jsx';
import RunsCreatures from './zones/NearBankRuns/RunsCreatures.jsx';
import FlowCreatures from './zones/MainChannelFlow/FlowCreatures.jsx';
import ShallowsCreatures from './zones/MarginalShallows/ShallowsCreatures.jsx';
import FaunaIcons from './FaunaIcons.jsx';
import FaunaModal from './modals/FaunaModal.jsx';
import CreatureModal from './modals/CreatureModal.jsx';

/** Parse boolean-ish env vars robustly, defaulting to true unless explicitly "false"/"0"/"off". */
const boolEnv = (key, defaultVal = true) => {
  const raw = process.env[key];
  if (raw == null) return defaultVal;
  const v = String(raw).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(v)) return true;
  if (['0', 'false', 'no', 'off', ''].includes(v)) return false;
  return defaultVal;
};

/**
 * ZoneSection component with underwater effects, particles, fauna features,
 * clickable swimming creatures opening a modal, highlight based on slider,
 * and sandy footer on the deepest zone.
 *
 * Props:
 *   - zone: { id, depthRange, bgGradient, darkBgGradient?, fauna[], versions[] }
 *   - theme: 'light' | 'dark'
 *   - highlightVersion?: string | null
 *   - allZones: array of all zone objects (used for maxDepth & to check “last zone”)
 */
export default function ZoneSection({
  zone,
  theme = 'light',
  highlightVersion,
  allZones,
}) {
  const [selectedFauna, setSelectedFauna] = useState(null);
  const [selectedCreature, setSelectedCreature] = useState(null);

  // Feature flags (robust + default-on)
  const showShallows = boolEnv('REACT_APP_SHOW_SHALLOWS_CREATURES', true);
  const showRuns     = boolEnv('REACT_APP_SHOW_RUNS_CREATURES', true);
  const showFlow     = boolEnv('REACT_APP_SHOW_FLOW_CREATURES', true);
  const showPools    = boolEnv('REACT_APP_SHOW_POOLS_CREATURES', true);
  const showTrench   = boolEnv('REACT_APP_SHOW_TRENCH_CREATURES', true);

  // Handler for when a creature is clicked
  const handleCreatureClick = (creature) => setSelectedCreature(creature);

  // 1) Compute the maximum depth across allZones
  const maxDepth = Math.max(...allZones.map((z) => z.depthRange[1]));
  const depthFrac = zone.depthRange[1] / maxDepth;

  // 2) Check if this is the last zone in the array
  const isLast = zone.id === allZones[allZones.length - 1].id;

  // 3) Random horizontal offsets for fauna icons
  const horizontalOffsets = useMemo(
    () => zone.fauna.map(() => Math.random() * 80 + 10),
    [zone.fauna]
  );

  // 4) Particle setup
  const particles = useMemo(() => {
    const maxCount = 50;
    const minCount = 10;
    const count = Math.round(maxCount * (1 - depthFrac)) + minCount;
    return Array.from({ length: count }).map(() => ({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 2}s`,
      size: `${Math.random() * 0.3 + 0.1}rem`, // 0.1–0.4rem
      axis: Math.random() > 0.5 ? 'X' : 'Y',
      duration:
        zone.id === 'shallows'
          ? 1.5
          : zone.id === 'runs'
          ? 2
          : zone.id === 'flow'
          ? 2.5
          : 3,
    }));
  }, [depthFrac, zone.id]);

  // 5) Overlay and fade masks
  const overlayOpacity = 0.2 + depthFrac * 0.5;
  const overlayColor =
    theme === 'light'
      ? `rgba(30,144,255,${overlayOpacity})`
      : `rgba(0,0,50,${overlayOpacity})`;
  const fadeTop =
    theme === 'light'
      ? 'linear-gradient(to bottom, white, transparent)'
      : 'linear-gradient(to bottom, #1f2937, transparent)';
  const fadeBottom =
    theme === 'light'
      ? 'linear-gradient(to top, white, transparent)'
      : 'linear-gradient(to top, #1f2937, transparent)';

  // 6) Choose gradient (dark or light)
  const gradient =
    theme === 'dark' && zone.darkBgGradient
      ? zone.darkBgGradient
      : zone.bgGradient;

  return (
    <section
      id={zone.id}
      className={`relative snap-start min-h-screen bg-gradient-to-b ${gradient} text-${
        theme === 'light' ? 'white' : 'gray-200'
      } px-4 py-32 overflow-hidden`}
    >
      {/* Particle keyframes */}
      <style>
        {`
          @keyframes driftX {
            0% { transform: translateX(0); opacity: 0.2 }
            50% { transform: translateX(5px); opacity: 0.5 }
            100% { transform: translateX(0); opacity: 0.2 }
          }
          @keyframes driftY {
            0% { transform: translateY(0); opacity: 0.2 }
            50% { transform: translateY(-5px); opacity: 0.5 }
            100% { transform: translateY(0); opacity: 0.2 }
          }
        `}
      </style>

      {/* Underwater overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: overlayColor }}
      />

      {/* Zone creatures */}
      {zone.id === 'shallows' && showShallows && (
        <ShallowsCreatures
          zone={zone}
          theme={theme}
          onCreatureClick={handleCreatureClick}
          highlightVersion={highlightVersion}
        />
      )}
      {zone.id === 'runs' && showRuns && (
        <RunsCreatures
          zone={zone}
          theme={theme}
          onCreatureClick={handleCreatureClick}
          highlightVersion={highlightVersion}
        />
      )}
      {zone.id === 'flow' && showFlow && (
        <FlowCreatures
          zone={zone}
          theme={theme}
          onCreatureClick={handleCreatureClick}
          highlightVersion={highlightVersion}
        />
      )}
      {zone.id === 'pools' && showPools && (
        <DeepCreatures
          zone={zone}
          theme={theme}
          onCreatureClick={handleCreatureClick}
          highlightVersion={highlightVersion}
        />
      )}
      {zone.id === 'trench' && showTrench && (
        <TrenchCreatures
          zone={zone}
          theme={theme}
          onCreatureClick={handleCreatureClick}
          highlightVersion={highlightVersion}
        />
      )}

      {/* Particles drifting */}
      {particles.map((p, i) => (
        <div
          key={i}
          className="absolute bg-white pointer-events-none"
          style={{
            top: p.top,
            left: p.left,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.6)',
            animation: `drift${p.axis} ${p.duration}s linear ${p.delay} infinite`,
          }}
        />
      ))}

      {/* Fade masks */}
      <div
        className="absolute top-0 left-0 w-full h-24 pointer-events-none"
        style={{ background: fadeTop }}
      />
      <div
        className="absolute bottom-0 left-0 w-full h-24 pointer-events-none"
        style={{ background: fadeBottom }}
      />

      {/* Fauna icons */}
      <FaunaIcons
        fauna={zone.fauna}
        depthRange={zone.depthRange}
        theme={theme}
        highlightVersion={highlightVersion}
        horizontalOffsets={horizontalOffsets.map((off) => `${off}%`)}
        onClick={(faunaItem) => setSelectedFauna(faunaItem)}
      />

      {/* Sand bottom + Footer (only in the last zone) */}
      {isLast && (
        <div className="absolute bottom-0 left-0 w-full h-16 bg-yellow-200">
          <Footer theme={theme} />
        </div>
      )}

      {/* Fauna modal */}
      <FaunaModal
        fauna={selectedFauna}
        theme={theme}
        onClose={() => setSelectedFauna(null)}
      />

      {/* Creature modal */}
      {selectedCreature && (
        <CreatureModal
          creature={selectedCreature}
          theme={theme}
          onClose={() => setSelectedCreature(null)}
        />
      )}
    </section>
  );
}
